import { Link, type ErrorComponentProps } from "@tanstack/react-router";
import { Check, Copy, Mail, ShieldAlert } from "lucide-react";
import { useState } from "react";
import { APP_CONFIG } from "@/config/app-config";
import { AppError } from "@/domain/errors/common";
import Button, {
  buttonVariants,
} from "@/interface/components/design-system/button/button";
import { MainContainer } from "@/interface/components/layout/main-container";
import { isErr, safeTry } from "@/lib/result-pattern";
import { cn } from "@/lib/utils";

export function GlobalErrorPage({ error, reset }: ErrorComponentProps) {
  const supportEmail = "help@nutriflex.fit";
  const environment = APP_CONFIG.getCurrentEnvironment();
  const shouldShowErrorDetails =
    environment === "development" || environment === "staging";
  const errorMessage = getErrorMessage(error);
  const [copyStatus, setCopyStatus] = useState<"idle" | "success" | "error">(
    "idle"
  );

  async function handleCopySupportEmail() {
    if (typeof navigator === "undefined" || !("clipboard" in navigator)) {
      setCopyStatus("error");
      return;
    }

    const result = await safeTry(
      async () => {
        await navigator.clipboard.writeText(supportEmail);
      },
      () => new AppError("No se pudo copiar el correo de soporte")
    );

    if (isErr(result)) {
      setCopyStatus("error");
      return;
    }

    setCopyStatus("success");
    window.setTimeout(() => setCopyStatus("idle"), 2000);
  }

  return (
    <MainContainer>
      <main className="flex h-screen flex-col items-center justify-center">
        <div className="relative mx-auto w-full max-w-md space-y-8 px-4 text-center">
          <div className="mb-8">
            <h1 className="mt-6 text-6xl font-extrabold text-nt-neutral-950 dark:text-gray-100">
              <ShieldAlert className="mx-auto mb-4 h-16 w-16 text-nt-secondary" />
            </h1>
            <p className="mt-2 text-3xl font-bold text-nt-neutral-950 dark:text-gray-100">
              Algo salio mal
            </p>
            <p className="mt-2 text-sm text-nt-neutral-600 dark:text-gray-400">
              Ocurrio un error inesperado. Intenta nuevamente o vuelve al
              inicio.
            </p>
          </div>

          <div className="rounded-2xl border border-nt-secondary-200 bg-nt-secondary-50 p-4 text-center dark:border-nt-secondary-800 dark:bg-nt-secondary-950/20">
            <p className="font-semibold text-nt-neutral-900 dark:text-gray-100">
              Te ayudamos a resolverlo rapido.
            </p>
            <p className="mt-1 text-sm text-nt-neutral-700 dark:text-gray-300">
              Si necesitas ayuda, escribenos y te responderemos en menos de 12
              horas.
            </p>
            <div className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-nt-secondary">
              <Mail className="h-4 w-4" />
              <button
                type="button"
                onClick={() => void handleCopySupportEmail()}
                className="rounded-sm underline-offset-2 transition-colors duration-200 ease-in-out hover:text-nt-secondary-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nt-secondary focus-visible:ring-offset-2 dark:hover:text-nt-secondary-200">
                {supportEmail}
              </button>
              <button
                type="button"
                aria-label="Copiar correo de soporte"
                title="Copiar correo"
                onClick={() => void handleCopySupportEmail()}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors duration-200 ease-in-out hover:bg-nt-secondary-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nt-secondary focus-visible:ring-offset-2 dark:hover:bg-nt-secondary-900/40">
                {copyStatus === "success" ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </button>
            </div>
            <div className="mt-3 flex flex-col items-center justify-center gap-2 text-center sm:flex-row sm:items-center">
              <span
                className={cn(
                  "text-xs",
                  copyStatus === "error"
                    ? "text-red-600 dark:text-red-400"
                    : "text-nt-neutral-600 dark:text-gray-400"
                )}
                aria-live="polite">
                {copyStatus === "error"
                  ? "No pudimos copiarlo, pero puedes escribirnos al correo de arriba."
                  : copyStatus === "success"
                    ? "Correo copiado al portapapeles."
                    : "Soporte activo de lunes a domingo."}
              </span>
            </div>
          </div>

          <div className="mt-8 flex w-full flex-col gap-3 sm:flex-row">
            <Button
              type="button"
              onClick={() => reset()}
              className="w-full sm:flex-1">
              Reintentar
            </Button>

            <Link
              to="/"
              className={cn(
                buttonVariants({ variant: "tertiary" }),
                "w-full sm:flex-1"
              )}>
              Ir al inicio
            </Link>
          </div>

          {shouldShowErrorDetails && errorMessage ? (
            <details className="rounded-md border border-nt-neutral-300 bg-nt-neutral-50 p-4 text-left">
              <summary className="cursor-pointer text-sm font-medium text-nt-neutral-800">
                Detalles tecnicos (solo desarrollo)
              </summary>
              <pre className="mt-3 whitespace-pre-wrap break-words text-xs text-nt-neutral-700">
                {errorMessage}
              </pre>
            </details>
          ) : null}
        </div>
      </main>
    </MainContainer>
  );
}

function getErrorMessage(error: ErrorComponentProps["error"]) {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  return "";
}
